export type DelegationStatus = 'None' | 'Pending' | 'Approved';

export type ActivityRatingScale = 'NT' | 'PT' | 'FT';

// Skill rating scale: BT < OT- < OT < OT+ < AT < O, plus N/A
export type SkillRatingScale = 'BT' | 'OT_Minus' | 'OT' | 'OT_Plus' | 'AT' | 'O' | 'NA';

// Display mode for skill ratings
export type SkillDisplayMode = 'full' | 'simplified';

// Simplified rating for Appraisee/Mentor view
export type SimplifiedRating = 'ME' | 'ND' | 'NA';
// Which of these apply to a Job End is decided server-side and served as natureOfRoleActivities.
export type ActivityType =
  // Legacy set
  | 'DeskBasedResearch'
  | 'FieldBasedResearch'
  | 'InterviewingThirdParties'
  | 'FinancialModelling'
  | 'ExcelModelling'
  | 'DataManipulation'
  | 'AdvancedModelling'
  | 'CloseWorkingWithClient'
  | 'ManagementOfJrStaff'
  // Associate Consultant set
  | 'ResearchAndDataGathering'
  | 'DatasetAnalysis'
  | 'ProblemStructuring'
  | 'WrittenDeliverables'
  | 'PresentationInternal'
  | 'MarketFinancialModelling'
  | 'Interviewing'
  | 'PresentationExternal'
  | 'JuniorStaffSupport'
  // Consultant set
  | 'ComplexAnalysisModelling'
  | 'Storyline'
  | 'PresentationToClients'
  | 'ClientCollaboration'
  | 'JuniorStaffManagement';

export interface ActivityRating {
  activityType: ActivityType;
  rating: ActivityRatingScale | null;
}

// Basic skill info (without descriptions - for initial load)
export interface SkillBasic {
  skillId: number;
  skillName: string;
  sortOrder: number;
  rating: SkillRatingScale | null;
}

// Full skill details with descriptions (for lazy loading)
export interface SkillDetail {
  skillId: number;
  skillName: string;
  btDescription: string | null;
  otDescription: string | null;
  atDescription: string | null;
  oDescription: string | null;
  sortOrder: number;
  rating: SkillRatingScale | null;
}

// Skills grouped by area
export interface SkillArea {
  areaId: number;
  areaName: string;
  mainArea: string | null;
  skills: SkillBasic[];
}

// Alias for backward compatibility in UI components
export type SkillWithRating = SkillBasic & Partial<Pick<SkillDetail, 'btDescription' | 'otDescription' | 'atDescription' | 'oDescription'>>;

// Container for skill ratings data
export interface SkillRatings {
  overallRating: SkillRatingScale | null;
  overallStandardsAreaId: number | null;
  displayMode: SkillDisplayMode;
  skillAreas: SkillArea[];
}

export type JobEndStatus = 'NotStarted' | 'Started' | 'Finalised' | 'Delivered' | 'Expired' | 'Deleted';

export type EmailSentStatus = 'Pending' | 'Sent' | 'Failed' | 'NotApplicable';

export interface JobEndDetail {
  id: number;
  status: JobEndStatus;
  dueDate: string;
  emailSent: EmailSentStatus;
  collatorNotified: EmailSentStatus;
  createdAt: string;
  updatedAt: string | null;
  rowVersion: string;

  appraiseeStaffId: number;
  appraiseeFirstName: string;
  appraiseeLastName: string;
  appraiseeEmail: string;
  appraiseeGrade: string;

  collatorStaffId: number;
  collatorFirstName: string;
  collatorLastName: string;
  collatorEmail: string;

  projectCode: string;
  projectName: string;
  clientName: string;
  assignmentStartDate: string;
  assignmentEndDate: string | null;
  tenureInGrade: string;
  
  submissionGateOpensOn: string | null;
  seniorFeedbackEarliestDeadline: string | null;

  delegationStatus: DelegationStatus;
  delegateStaffId: number | null;
  delegateFirstName: string | null;
  delegateLastName: string | null;
  delegateEmail: string | null;
  delegateExpiry: string | null;

  natureOfRoleActivities?: ActivityType[];
  activityRatings?: ActivityRating[];
  strengthFeedbacks?: StrengthFeedback[];
  developmentAreaFeedbacks?: DevelopmentAreaFeedback[];
  skillRatings?: SkillRatings | null;
  seniorFeedbacks?: SeniorFeedbackReviewer[];
}

export interface EligibleDelegate {
  staffId: number;
  firstName: string;
  lastName: string;
  email: string;
  grade: string;
}

export interface DelegateRequest {
  delegateStaffId: number;
}

export interface StrengthFeedback {
  id: number;
  content: string;
  orderIndex: number;
}

export interface DevelopmentAreaFeedback {
  id: number;
  content: string;
  orderIndex: number;
}

export interface FeedbackItemRequest {
  id?: number;
  content: string;
  orderIndex: number;
}


// Request DTOs for skill ratings updates
export interface SkillRatingItem {
  skillId: number;
  rating: SkillRatingScale | null;
}

export interface UpdateSkillRatingsRequest {
  ratings: SkillRatingItem[];
}

export interface UpdateOverallSkillRatingRequest {
  rating: SkillRatingScale | null;
}

// Helper function to convert rating to simplified display
export function toSimplifiedRating(rating: SkillRatingScale | null): SimplifiedRating {
  if (rating === null || rating === 'NA') return 'NA';
  if (rating === 'BT' || rating === 'OT_Minus') return 'ND';
  return 'ME'; // OT, OT_Plus, AT, O all map to ME (Meets Expectations)
}

// Shared simplified-bucket display maps (consumed by SkillRatingsSection and RatingBadgeChip)
export const SIMPLIFIED_LABELS: Record<SimplifiedRating, string> = {
  ME: 'Meets Expectations',
  ND: 'Needs Development',
  NA: 'Not Applicable',
};

export const SIMPLIFIED_COLORS: Record<SimplifiedRating, 'success' | 'info' | 'default'> = {
  ME: 'success',
  ND: 'info',
  NA: 'default',
};

// Type guard: narrows an arbitrary string to a known SkillRatingScale token so
// callers can safely invoke toSimplifiedRating without loosening its signature.
const SKILL_RATING_SCALES: readonly SkillRatingScale[] = [
  'BT',
  'OT_Minus',
  'OT',
  'OT_Plus',
  'AT',
  'O',
  'NA',
];

export function isKnownScale(code: string | null): code is SkillRatingScale {
  return code !== null && (SKILL_RATING_SCALES as readonly string[]).includes(code);
}

export type SeniorFeedbackStatus = 'Pending' | 'Submitted' | 'Expired';

export interface SeniorFeedbackReviewer {
  id: number;
  jobEndId: number;
  reviewerStaffId: number;
  reviewerName: string;
  requestedByStaffId: number;
  requestedByName: string;
  dueDate: string;
  status: SeniorFeedbackStatus;
  comments: string;
  isEditedByCollator: boolean;
  isDeletedByCollator: boolean;
}

export interface AddSeniorReviewerRequest {
  reviewerStaffId: number;
  dueDate: string;
}

export interface UpdateSeniorFeedbackRequest {
  comments: string;
}

export interface EligibleSeniorReviewer {
  staffId: number;
  firstName: string;
  lastName: string;
  email: string;
  grade: string;
}

// Active Job Ends Admin types

export interface ActiveJobEnd {
  id: number;
  projectName: string;
  dueDate: string;
  performanceFlag: string | null;
  appraiseeStaffId: number;
  appraiseeName: string;
  geo: string;
  tenureInGrade: string;
  jobEndAuthor: string;
  status: JobEndStatus;
}

export interface ActiveJobEndsFilterRequest {
  page: number;
  pageSize: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  projectName?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  performanceFlag?: string;
  appraiseeName?: string;
  geo?: string;
  tenureInGrade?: string;
  jobEndAuthor?: string;
  status?: JobEndStatus;
}

export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface BulkActionResult {
  processedCount: number;
  skippedIds?: number[];
}

export interface EditJobEndAuthorRequest {
  newCollatorStaffId: number;
}

export interface BulkEditJobEndAuthorRequest {
  jobEndIds: number[];
  newCollatorStaffId: number;
}

export interface BulkJobEndIdsRequest {
  jobEndIds: number[];
}
